// src/Pages/Dashboard/Dashboard.js
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Sidebar from "../../Component/Sidebar/Sidebar";
import Header from "../../Component/Header/Header";
import BudgetCards from "../../Component/BudgetCards/BudgetCards";
import TransactionDetail from "../../Component/TransactionDetail/TransactionDetail";
import Transactiontable from "../../Component/TransactionTableDashboard/TransactionTableDashboard";
import SpendingCharts from "../../Component/SpendingCharts/SpendingCharts";
import SpendingPrediction from "../../Component/SpendingPrediction/SpendingPrediction";
import PredictionTrend from "../../Component/SpendingPrediction/PredictionTrend";
import "./Dashboard.css";

const API_BASE_URL = process.env.REACT_APP_EXPRESS_API;
const API_URL = `${API_BASE_URL}/api/transactions`;
const POINTS_URL = `${API_BASE_URL}/api/transactions/poins`;
const MOBILE_BP = 1024;

function Dashboard() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= MOBILE_BP : false
  );

  const [transactions, setTransactions] = useState([]);
  const [monthlyPoints, setMonthlyPoints] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let rid;
    const onResize = () => {
      cancelAnimationFrame(rid);
      rid = requestAnimationFrame(() => {
        const mobile = window.innerWidth <= MOBILE_BP;
        setIsMobile(mobile);
      });
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      cancelAnimationFrame(rid);
    };
  }, []);

  useEffect(() => {
    const sidebarEl = document.querySelector(".dashboard-page .sidebar");
    const overlayEl = document.querySelector(".sidebar-overlay");
    const openMobile = isMobile && isSidebarOpen;

    if (sidebarEl) sidebarEl.classList.toggle("is-mobile-open", !!openMobile);
    if (overlayEl) overlayEl.classList.toggle("is-visible", !!openMobile);
    document.body.classList.toggle("lock-scroll", !!openMobile);

    if (!isMobile) {
      document.body.classList.remove("lock-scroll");
      overlayEl?.classList.remove("is-visible");
    }
  }, [isMobile, isSidebarOpen]);

  const toggleSidebar = () => setIsSidebarOpen((s) => !s);
  const closeSidebarMobile = () => {
    if (isMobile) setIsSidebarOpen(false);
  };

  // Fetch transaksi
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await fetch(API_URL, { credentials: "include" });
        if (!res.ok) throw new Error("Gagal mengambil data transaksi");
        const raw = await res.json();
        const list = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.transactions)
            ? raw.transactions
            : Array.isArray(raw?.data)
              ? raw.data
              : [];
        setTransactions(list);
        setError("");
      } catch (e) {
        setError(e?.message || "Terjadi kesalahan");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Fetch poin 30 hari
  useEffect(() => {
    (async () => {
      try {
        const now = new Date();
        const endDate = now.toISOString().split("T")[0];
        const start = new Date();
        start.setDate(now.getDate() - 30);
        const startDate = start.toISOString().split("T")[0];

        const res = await fetch(
          `${POINTS_URL}?startDate=${startDate}&endDate=${endDate}`,
          { credentials: "include" }
        );
        if (!res.ok) return;
        const data = await res.json();
        const monthly =
          Array.isArray(data) && data.length
            ? data[0].totalPoints
            : data?.totalPoints || 0;
        setMonthlyPoints(Number(monthly) || 0);
      } catch {
        // abaikan error poin
      }
    })();
  }, []);

  const getDate = (t) =>
    new Date(t?.date || t?.tanggal || t?.createdAt || t?.updatedAt || 0);

  const sorted = useMemo(
    () =>
      Array.isArray(transactions)
        ? [...transactions].sort((a, b) => getDate(b) - getDate(a))
        : [],
    [transactions]
  );

  const latestTransaction = sorted[0] || null;
  const recentTransactions = sorted.slice(0, 10);

  const mainContentClass =
    !isMobile && isSidebarOpen ? "sidebar-open" : "sidebar-closed";

  return (
    <div className="dashboard-page">
      <Sidebar isOpen={isSidebarOpen} />
      <div className="sidebar-overlay" onClick={closeSidebarMobile} />
      <div className={`main-content ${mainContentClass}`}>
        <Header toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} />
        <div className="page-container">
          <div className="dashboard-content-area">
            <div className="dashboard-header">
              <div className="breadcrumb-ds">
                <span className="breadcrumb-item">Halaman</span>
                <span className="breadcrumb-separator">/</span>
                <span className="breadcrumb-item active">Dasbor</span>
              </div>
              <h1 className="dashboard-title">Dasboard</h1>
            </div>

            {loading && <p>Memuat data transaksi…</p>}
            {!loading && error && <p style={{ color: "red" }}>{error}</p>}

            <section className="panel budget-section">
              <BudgetCards />
            </section>

            <section className="activity-section">
              <div className="activity-grid-fixed">
                <div className="activity-card activity-text">
                  <h3>Keaktifan Pengguna</h3>
                  <p>
                    Catat rutinitas finansialmu, pantau progres, dan lihat
                    bagaimana kebiasaan kecil bisa membangun keuangan yang lebih
                    sehat.
                  </p>
                  <p>
                    Setiap aktivitas yang kamu lakukan akan memberimu{" "}
                    <strong>poin</strong> sebagai bentuk apresiasi. Poin bisa
                    ditukar dengan hadiah menarik!
                  </p>
                  <Link to="/poin" className="read-more-link">
                    Baca Selengkapnya <span>→</span>
                  </Link>
                </div>

                <div
                  className="activity-card points-card"
                  style={{ background: "#f59e0b" }}
                >
                  <div className="points-wrap">
                    <div className="label">Poin Bulan Ini</div>
                    <div className="value">{monthlyPoints}</div>
                  </div>
                </div>

                <div className="activity-card activity-image">
                  <img
                    src="https://github.com/user-attachments/assets/e62784b4-8049-4c6b-b5be-2606ddedd821"
                    alt="Berkumpul"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      borderRadius: 12,
                    }}
                  />
                </div>
              </div>
            </section>

            <section className="panel transaction-detail-section">
              <h2 className="section-title">Detail Transaksi</h2>
              <TransactionDetail
                transaction={latestTransaction}
                data={latestTransaction}
              />
              {!latestTransaction && !loading && !error && (
                <p>Belum ada transaksi.</p>
              )}
            </section>

            <section className="panel recent-expenses-section">
              <h3 className="section-title">Riwayat Pengeluaran Terakhir</h3>
              <Transactiontable
                transactions={recentTransactions}
                data={recentTransactions}
              />
              {!recentTransactions.length && !loading && !error && (
                <p>Tidak ada riwayat transaksi.</p>
              )}
            </section>

            <section className="panel data-visualization-section">
              <SpendingCharts transactions={transactions} />
            </section>

            <section className="panel prediction-section">
              <SpendingPrediction />
            </section>

            <section className="panel prediction-section">
              <PredictionTrend />
            </section>

            <footer className="dashboard-footer">
              <p>@copyright 2025 - LacaKas</p>
            </footer>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
